import { Injectable } from '@angular/core';
import { AuthService } from '@app/auth.service';
import { UserApiService } from '@app/api/user-api.service';
import Utils from '@app/utils/utils';

// NOTE - Leaving this enum here for future use
export enum UserPrivilegeEnum {
    VIEW_DEVICE = 1,
    ADD_REMOVE_DEVICES = 2,
    VIEW_ADD_REMOVE_ADDITIONAL_HUBS = 3,
    VIEW_OTHER_ACCOUNTS = 4,
    EDIT_CUSTOMER_ACCOUNT_DETAILS = 5,
    EDIT_AGILENT_ONLY_FIELDS = 6,
    ADD_REMOVE_CUSTOMER_ACCOUNT = 7,
    MANAGE_SYSTEM_USERS = 8,
    MANAGE_CUSTOMER_USERS = 9,
    VIEW_SERVER = 10,
    EDIT_SERVER_DETAILS = 11,
    MAKE_SOFTWARE_SELECTIONS = 12,
    ADD_REMOVE_SERVERS = 13,
    VIEW_ACTIVITY_LOGS = 14,
    VIEW_CID = 15,
    EDIT_CID_DETAILS = 16,
    MAKE_SOFTWARE_CHANGES = 17,
    CONFIGURE_CID_NICS = 18,
    LAUNCH_CDS_DESKTOP = 19,
    LAUNCH_COCKPIT = 20,
    GRANT_REVOKE_REMOTE_ACCESS = 21,
    RESTART_SHUTDOWN_CID = 22,
    REGISTER_CDS = 23,
    RESET_OPENLAB_CDS = 24,
    ADD_REMOVE_CIDS = 25,
    ALLOW_CHANGES = 26,  // 'CID' privilege
    SERVER_ALLOW_DISALLOW_CHANGES = 27,  // 'Server' privilege
    MANAGE_WINDOWS_UPDATES = 28
}

@Injectable({
    providedIn: 'root'
})
export class UserService {

    private AUTH_USER_STORAGE_KEY = 'UserService.authUser';

    constructor(
        private userApiService: UserApiService,
        private authService: AuthService
    ) {
        //
    }

    /**
     * Returns the currently logged in user from local storage.
     * If there is no cached user, the user is retrieved from
     * the API and saved to local storage before being returned.
     */
    public async getAuthUser() {
        let user = JSON.parse(localStorage.getItem(this.AUTH_USER_STORAGE_KEY));
        if (!user) {
            const resp = await this.userApiService.getAuthenticatedUser();
            user = resp.user;
            localStorage.setItem(this.AUTH_USER_STORAGE_KEY, JSON.stringify(user));
        }
        return user;
    }

    public async saveAuthUser(user) {
        const resp = await this.userApiService.updateUser({user: user});
        localStorage.setItem(this.AUTH_USER_STORAGE_KEY, JSON.stringify(resp.user));
    }

    public async getAuthUserColumnPrefs(pageKey) {
        return {
            getColumnWidths: async () => {
                const user = await this.getAuthUser();
                return user?.user_prefs?.gridColumnWidths?.[pageKey] || {};
            },
            saveColumnWidths: async (columnWidths) => {
                const user = await this.getAuthUser();

                // Make sure preferences structure exists on the user
                Utils.createNestedEmptyObjects(
                    user,
                    [ 'user_prefs', 'gridColumnWidths', pageKey ]);

                for (const key of Object.keys(columnWidths)) {
                    user.user_prefs.gridColumnWidths[pageKey][key] = columnWidths[key];
                }
                await this.saveAuthUser(user);
            }
        };
    }

    /**
     * Clears the currently logged in user from local storage, then
     * logs out.
     */
    public logOutUser(isTimeout: boolean = false) {
        localStorage.removeItem(this.AUTH_USER_STORAGE_KEY);
        this.authService.logOut(isTimeout);
    }

    public async userHasPrivs(req_priv_ids: number[], hub_cust_id: number) {
        const user = await this.getAuthUser();
        const user_privs = user.user_privileges;
        const user_privs_for_customer = user_privs.find(item => item.cust_id === hub_cust_id);
        // Does user have any privs for this customer hub?
        if (!user_privs_for_customer) {
            return false;
        }
        const user_priv_ids_for_customer = Object.values(user_privs_for_customer.priv_ids);
        const missing_priv_ids = req_priv_ids.filter(id => !user_priv_ids_for_customer.includes(id));
        // Is user missing any privs to perform this action in this customer's hub?
        if (missing_priv_ids && missing_priv_ids.length > 0) {
            return false;
        }
        return true;
    }
}
